import { useState, useCallback } from 'react';
import { 
  Filter, 
  MapPin, 
  Eye, 
  Grid3X3, 
  List,
  RefreshCw,
  AlertCircle,
  Image as ImageIcon,
  Loader2,
  Database,
  Edit,
  Play,
  Search,
  X,
  Save,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui';
import { useLibraryAssets } from './hooks/useLibraryAssets';
import { LibraryAsset, LibraryFilters as LibraryFiltersType } from './types/library';
import { PROGRAM_OPTIONS } from './constants';
import { LocationFilter } from './components/LocationFilter';
import { getResizedImageUrl } from './utils/imageUrl';

export const AssetLibrary = () => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState<LibraryAsset | null>(null);
  const [editingAsset, setEditingAsset] = useState<LibraryAsset | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    thumbnail_url: '',
    source: '',
    program_id: '',
    author_id: '',
    event: ''
  });
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [filters, setFilters] = useState<LibraryFiltersType>({
    page: 1,
    page_size: 10,
    group: 1,
    group_size: 10
  });

  // Asset Library API hook
  const {
    assets,
    villages,
    loading,
    loadingMore,
    error,
    hasMoreVillages,
    refetch,
    updateFilters,
    loadMoreVillages,
    loadMoreAssetsForVillage,
    updateAsset
  } = useLibraryAssets(filters);

  // Update filters when they change
  const handleFiltersChange = useCallback((newFilters: Partial<LibraryFiltersType>) => {
    const updatedFilters = { ...filters, ...newFilters, page: 1 };
    console.log('Filter change:', newFilters, 'Updated filters:', updatedFilters);
    setFilters(updatedFilters);
    updateFilters(updatedFilters);
  }, [filters, updateFilters]);

  // Handle asset click for detailed view
  const handleAssetClick = useCallback((asset: LibraryAsset) => {
    setSelectedAsset(asset);
  }, []);

  // Handle asset edit
  const handleAssetEdit = useCallback((asset: LibraryAsset) => {
    setEditingAsset(asset);
    setEditForm({
      title: asset.title || '',
      description: asset.description || '',
      thumbnail_url: asset.thumbnail_url || '',
      source: asset.source || '',
      program_id: asset.program || '', // Use 'program' from asset, not 'program_id'
      author_id: asset.author_id || '',
      event: asset.event || ''
    });
    setEditError(null);
  }, []);

  // Close modals
  const handleCloseDetailModal = useCallback(() => {
    setSelectedAsset(null);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditingAsset(null);
    setEditForm({
      title: '',
      description: '',
      thumbnail_url: '',
      source: '',
      program_id: '',
      author_id: '',
      event: ''
    });
    setEditError(null);
  }, []);

  // Handle form input changes
  const handleFormChange = useCallback((field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Save asset changes
  const handleSaveAsset = useCallback(async () => {
    if (!editingAsset) return;

    try {
      setSaving(true);
      setEditError(null);

      // Prepare update data (only include non-empty fields)
      const updateData: any = {};
      Object.entries(editForm).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          updateData[key] = value.trim();
        }
      });

      // If no changes, close modal
      if (Object.keys(updateData).length === 0) {
        handleCloseEditModal();
        return;
      }

      const success = await updateAsset(editingAsset.id, updateData);
      
      if (success) {
        handleCloseEditModal();
        // Show success message (you could add a toast here)
        console.log('Asset updated successfully');
      } else {
        setEditError('Failed to update asset. Please try again.');
      }
    } catch (error) {
      console.error('Error saving asset:', error);
      setEditError('An error occurred while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editingAsset, editForm, updateAsset, handleCloseEditModal]);

  // Format date for display
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  // Check if asset is video type (case-insensitive)
  const isVideo = (type: string) => type?.toLowerCase() === 'video';

  // Get media type icon
  const getMediaIcon = (type: string) => {
    if (isVideo(type)) {
      return <Play className="h-4 w-4" />;
    }
    return <ImageIcon className="h-4 w-4" />;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Asset Library</h1>
          <p className="text-gray-600 mt-1">
            Browse and manage assets in the library with comprehensive metadata
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <Button
            variant="secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            variant="secondary"
            onClick={refetch}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Filter className="h-5 w-5 mr-2" />
              Filter Assets
            </h3>
            <button
              onClick={() => setShowFilters(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Location Section */}
            <div>
              <LocationFilter
                value={{
                  district: filters.district,
                  subdistrict: filters.subdistrict_name,
                  village_id: filters.village_id,
                  village_name: filters.village_name
                }}
                onChange={(location) => handleFiltersChange({
                  district: location.district,
                  subdistrict_name: location.subdistrict,
                  village_id: location.village_id,
                  village_name: location.village_name
                })}
                showVillages={true}
              />
            </div>

            {/* Program & Activity Section */}
            <div>
              <div className="flex items-center mb-4">
                <Database className="h-5 w-5 text-gray-400 mr-2" />
                <h4 className="font-medium text-gray-900">Program & Activity</h4>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Program
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    value={filters.program || ''}
                    onChange={(e) => handleFiltersChange({ program: e.target.value || undefined })}
                  >
                    <option value="">All Programs</option>
                    {PROGRAM_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Activity
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., SD Attendance"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    value={filters.event || ''}
                    onChange={(e) => handleFiltersChange({ event: e.target.value || undefined })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Type
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    value={filters.asset_type || ''}
                    onChange={(e) => handleFiltersChange({ asset_type: e.target.value || undefined })}
                  >
                    <option value="">All Types</option>
                    <option value="Image">Image</option>
                    <option value="Video">Video</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Date & Search Section */}
            <div>
              <div className="flex items-center mb-4">
                <Search className="h-5 w-5 text-gray-400 mr-2" />
                <h4 className="font-medium text-gray-900">Date & Search</h4>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    value={filters.date_from || ''}
                    onChange={(e) => handleFiltersChange({ date_from: e.target.value || undefined })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    value={filters.date_to || ''}
                    onChange={(e) => handleFiltersChange({ date_to: e.target.value || undefined })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset ID
                  </label>
                  <input
                    type="text"
                    placeholder="Search by asset ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    value={filters.asset_id || ''}
                    onChange={(e) => handleFiltersChange({ asset_id: e.target.value || undefined })}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Pagination Settings */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h4 className="font-medium text-gray-900 mb-4">Pagination Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Villages per page
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={filters.page_size || 10}
                  onChange={(e) => handleFiltersChange({ page_size: parseInt(e.target.value) })}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assets per village
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={filters.group_size || 10}
                  onChange={(e) => handleFiltersChange({ group_size: parseInt(e.target.value) })}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-between">
            <Button
              variant="secondary"
              onClick={() => {
                const resetFilters = { page: 1, page_size: 10, group: 1, group_size: 10 };
                setFilters(resetFilters);
                handleFiltersChange(resetFilters);
              }}
            >
              Reset All
            </Button>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowFilters(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowFilters(false)}
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">View:</span>
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="px-3 py-1"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="px-3 py-1"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Results count */}
        <div className="text-sm text-gray-600">
          {loading ? (
            'Loading assets...'
          ) : error ? (
            <span className="text-red-600">Error loading assets</span>
          ) : (
            `${assets.length} assets in ${villages.length} villages`
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading assets from library...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
            <div>
              <h3 className="font-medium text-red-800">Error Loading Assets</h3>
              <p className="text-red-700 mt-1">{error}</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={refetch}
                className="mt-2"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && villages.length === 0 && (
        <div className="text-center py-12">
          <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Assets Found</h3>
          <p className="text-gray-600 mb-4">
            No assets match your current filters. Try adjusting your search criteria.
          </p>
          <Button variant="secondary" onClick={refetch}>
            Refresh Assets
          </Button>
        </div>
      )}

      {/* Assets Content */}
      {!loading && !error && villages.length > 0 && (
        <div className="space-y-8">
          {villages.map((village) => (
            <div key={village.village_id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Village Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400" />
                    <div>
                      <h3 className="font-medium text-gray-900">{village.village_name}</h3>
                      <p className="text-sm text-gray-600">
                        {village.subdistrict_name}, {village.district_name}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {village.assets.length} assets
                  </div>
                </div>
              </div>

              {/* Assets Grid/List */}
              <div className="p-6">
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {village.assets.map((asset) => (
                      <div
                        key={asset.id}
                        className="group relative bg-white border border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleAssetClick(asset)}
                      >
                        {/* Thumbnail */}
                        <div className="aspect-square bg-gray-100 relative">
                          {asset.thumbnail_url || asset.cdn_url ? (
                            isVideo(asset.type) ? (
                              <video
                                src={asset.cdn_url || asset.thumbnail_url}
                                poster={getResizedImageUrl(asset.thumbnail_url, 400)}
                                muted
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <img
                                src={getResizedImageUrl(asset.thumbnail_url || asset.cdn_url, 400)}
                                alt={asset.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent) {
                                    parent.className = 'aspect-square bg-gray-100 flex items-center justify-center';
                                    parent.innerHTML = `
                                      <div class="text-center">
                                        <svg class="w-12 h-12 text-gray-400 mx-auto mb-2" stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round">
                                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                          <polyline points="21,15 16,10 5,21"></polyline>
                                        </svg>
                                        <p class="text-xs text-gray-500">No Preview</p>
                                      </div>
                                    `;
                                  }
                                }}
                              />
                            )
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="text-center">
                                {getMediaIcon(asset.type)}
                                <p className="text-xs text-gray-500 mt-1">No Preview</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Media type indicator */}
                          <div className="absolute top-2 left-2">
                            <div className="bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs flex items-center">
                              {getMediaIcon(asset.type)}
                              <span className="ml-1 capitalize">{asset.type}</span>
                            </div>
                          </div>

                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                            <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <div className="bg-white rounded-full p-2 shadow-lg">
                                <Eye className="h-4 w-4 text-gray-700" />
                              </div>
                              <button
                                className="bg-white rounded-full p-2 shadow-lg"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssetEdit(asset);
                                }}
                              >
                                <Edit className="h-4 w-4 text-gray-700" />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Asset Info */}
                        <div className="p-3">
                          <h4 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">
                            {asset.title}
                          </h4>
                          {asset.event && (
                            <p className="text-xs text-gray-600 mb-1 line-clamp-1">{asset.event}</p>
                          )}
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{asset.program}</span>
                            <span>{formatDate(asset.captured_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* List View */
                  <div className="space-y-3">
                    {village.assets.map((asset) => (
                      <div
                        key={asset.id}
                        className="flex items-center space-x-4 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleAssetClick(asset)}
                      >
                        {/* Thumbnail */}
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                            {asset.thumbnail_url || asset.cdn_url ? (
                              isVideo(asset.type) ? (
                                <video
                                  src={asset.cdn_url || asset.thumbnail_url}
                                  poster={getResizedImageUrl(asset.thumbnail_url, 100)}
                                  muted
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <img
                                  src={getResizedImageUrl(asset.thumbnail_url || asset.cdn_url, 100)}
                                  alt={asset.title}
                                  className="w-full h-full object-cover"
                                />
                              )
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                {getMediaIcon(asset.type)}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Asset Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-900 truncate">{asset.title}</h4>
                            <button
                              className="ml-2 p-1 hover:bg-gray-200 rounded"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssetEdit(asset);
                              }}
                            >
                              <Edit className="h-4 w-4 text-gray-500" />
                            </button>
                          </div>
                          <p className="text-sm text-gray-600 truncate mt-1">{asset.description || asset.event}</p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center">
                              {getMediaIcon(asset.type)}
                              <span className="ml-1 capitalize">{asset.type}</span>
                            </span>
                            <span>{asset.program}</span>
                            {asset.event && asset.description && (
                              <span className="truncate">{asset.event}</span>
                            )}
                            <span>{formatDate(asset.captured_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Load More Assets for Village */}
                {village.has_next_page && (
                  <div className="mt-6 text-center">
                    <Button
                      variant="secondary"
                      onClick={() => loadMoreAssetsForVillage(village.village_id)}
                      disabled={loadingMore}
                      className="px-6"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Loading More Assets...
                        </>
                      ) : (
                        <>
                          Load More Assets ({village.assets.length} shown)
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Load More Villages Button */}
          {hasMoreVillages && (
            <div className="text-center">
              <Button
                variant="primary"
                onClick={loadMoreVillages}
                disabled={loadingMore}
                size="lg"
                className="px-8"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Loading More Villages...
                  </>
                ) : (
                  <>
                    Load More Villages ({villages.length} shown)
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Asset Detail Modal */}
      {selectedAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">Asset Details</h2>
              <button
                onClick={handleCloseDetailModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
              {/* Asset Preview */}
              <div className="lg:w-2/3 bg-gray-50 flex items-center justify-center p-6">
                <div className="max-w-full max-h-full">
                  {selectedAsset.thumbnail_url || selectedAsset.cdn_url ? (
                    isVideo(selectedAsset.type) ? (
                      <video
                        src={selectedAsset.cdn_url || selectedAsset.thumbnail_url}
                        poster={getResizedImageUrl(selectedAsset.thumbnail_url, 800)}
                        controls
                        className="max-w-full max-h-96 rounded-lg shadow-lg"
                      />
                    ) : (
                      <img
                        src={getResizedImageUrl(selectedAsset.thumbnail_url || selectedAsset.cdn_url, 800)}
                        alt={selectedAsset.title}
                        className="max-w-full max-h-96 object-contain rounded-lg shadow-lg"
                      />
                    )
                  ) : (
                    <div className="w-64 h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        {getMediaIcon(selectedAsset.type)}
                        <p className="text-gray-500 mt-2">No Preview Available</p>
                      </div>
                    </div>
                  )}

                  {selectedAsset.cdn_url && (
                    <div className="mt-4 text-center">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => window.open(selectedAsset.cdn_url, '_blank')}
                      >
                        {isVideo(selectedAsset.type) ? 'Open Video' : 'View Full Resolution'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Asset Metadata */}
              <div className="lg:w-1/3 p-6 overflow-y-auto">
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Title</label>
                    <p className="text-lg font-semibold text-gray-900 mt-1">{selectedAsset.title}</p>
                  </div>

                  {/* Description */}
                  {selectedAsset.description && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Description</label>
                      <p className="text-gray-900 mt-1">{selectedAsset.description}</p>
                    </div>
                  )}

                  {/* Event */}
                  {selectedAsset.event && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Event</label>
                      <p className="text-gray-900 mt-1">{selectedAsset.event}</p>
                    </div>
                  )}

                  {/* Technical Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Type</label>
                      <div className="flex items-center mt-1">
                        {getMediaIcon(selectedAsset.type)}
                        <span className="ml-2 text-gray-900 capitalize">{selectedAsset.type}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Source</label>
                      <p className="text-gray-900 mt-1">{selectedAsset.source}</p>
                    </div>
                  </div>

                  {/* Program Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Program</label>
                      <p className="text-gray-900 mt-1">{selectedAsset.program}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Captured Date</label>
                      <p className="text-gray-900 mt-1">{formatDate(selectedAsset.captured_at)}</p>
                    </div>
                  </div>

                  {/* File Info */}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Filename</label>
                    <p className="text-gray-900 mt-1 font-mono text-sm break-all">{selectedAsset.filename}</p>
                  </div>

                  {/* IDs */}
                  <div className="space-y-2">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Asset ID</label>
                      <p className="text-gray-900 mt-1 font-mono text-sm">{selectedAsset.asset_id}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Library ID</label>
                      <p className="text-gray-900 mt-1 font-mono text-sm">{selectedAsset.id}</p>
                    </div>
                    {selectedAsset.bf_asset_id && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">BF Asset ID</label>
                        <p className="text-gray-900 mt-1 font-mono text-sm">{selectedAsset.bf_asset_id}</p>
                      </div>
                    )}
                  </div>

                  {/* Author */}
                  {selectedAsset.author_id && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Author</label>
                      <p className="text-gray-900 mt-1">{selectedAsset.author_id}</p>
                    </div>
                  )}

                  {/* URLs */}
                  <div className="space-y-2">
                    {selectedAsset.cdn_url && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">CDN URL</label>
                        <p className="text-gray-900 mt-1 font-mono text-xs break-all">
                          <a
                            href={selectedAsset.cdn_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {selectedAsset.cdn_url}
                          </a>
                        </p>
                      </div>
                    )}
                    {selectedAsset.thumbnail_url && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Thumbnail URL</label>
                        <p className="text-gray-900 mt-1 font-mono text-xs break-all">
                          <a
                            href={selectedAsset.thumbnail_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {selectedAsset.thumbnail_url}
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-between flex-shrink-0">
              <Button
                variant="primary"
                onClick={() => {
                  handleCloseDetailModal();
                  handleAssetEdit(selectedAsset);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Asset
              </Button>
              <Button variant="secondary" onClick={handleCloseDetailModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Asset Edit Modal */}
      {editingAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">Edit Asset</h2>
              <button
                onClick={handleCloseEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {editError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
                    <div>
                      <h3 className="font-medium text-red-800">Error</h3>
                      <p className="text-red-700 mt-1">{editError}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Asset Preview */}
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                    {editingAsset.thumbnail_url || editingAsset.cdn_url ? (
                      <img
                        src={getResizedImageUrl(editingAsset.thumbnail_url || editingAsset.cdn_url, 100)}
                        alt={editingAsset.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {getMediaIcon(editingAsset.type)}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{editingAsset.title}</h3>
                    <p className="text-sm text-gray-500">ID: {editingAsset.id}</p>
                    <div className="flex items-center mt-1">
                      <span className="text-xs text-gray-500 capitalize">{editingAsset.type}</span>
                      {editingAsset.cdn_url && (
                        <a
                          href={editingAsset.cdn_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-xs text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          View
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter asset title"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter asset description"
                  />
                </div>

                {/* Event */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event
                  </label>
                  <input
                    type="text"
                    value={editForm.event}
                    onChange={(e) => handleFormChange('event', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., SD Attendance"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Source */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Source
                    </label>
                    <select
                      value={editForm.source}
                      onChange={(e) => handleFormChange('source', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Source</option>
                      <option value="DAM">DAM</option>
                      <option value="Impact">Impact</option>
                      <option value="IMPACT_APP">Impact App</option>
                      <option value="MANUAL_UPLOAD">Manual Upload</option>
                    </select>
                  </div>

                  {/* Program ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Program ID
                    </label>
                    <select
                      value={editForm.program_id}
                      onChange={(e) => handleFormChange('program_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Program</option>
                      {PROGRAM_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Author ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author ID
                  </label>
                  <input
                    type="text"
                    value={editForm.author_id}
                    onChange={(e) => handleFormChange('author_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter author identifier"
                  />
                </div>

                {/* Thumbnail URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail URL
                  </label>
                  <input
                    type="url"
                    value={editForm.thumbnail_url}
                    onChange={(e) => handleFormChange('thumbnail_url', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/thumbnail.jpg"
                  />
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-between flex-shrink-0">
              <Button
                variant="secondary"
                onClick={handleCloseEditModal}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveAsset}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};